import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Input, Row, Select, Typography, message } from "antd";
import axios from "axios";
import { PRIMARY_RED } from "constants/colors";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;
const { Option } = Select;

const orderStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Dispatched", value: "dispatched" },
  { label: "In transit", value: "in transit" },
  { label: "Delivered", value: "delivered" },
  { label: "Rejected", value: "rejected" },
];

const AddOrder = () => {
  const navigate = useNavigate();

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = authUser?.token;
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  const [products, setProducts] = useState([{ productId: null, quantity: 1, category: null }]);
  const [productOptions, setProductOptions] = useState([]);
  const [orderStatus, setOrderStatus] = useState(null);
  const [requestFrom, setRequestFrom] = useState(null);
  const [requestFromOptions, setRequestFromOptions] = useState([]);
  const [address, setAddress] = useState(null);
  const [addressOptions, setAddressOptions] = useState([]);
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    if (!token) {
      message.error("Session expired, please login again");
      return;
    }

    axios.get(`${process.env.REACT_APP_API_BASEURL}/product`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then((res) => {
      const options = res.data.map((item) => ({
        label: item.productName,
        value: item._id,
        category: item.productCategory,
        images: item.productImgUrl,
      }));
      setProductOptions(options);
    }).catch(() => message.error("Failed to load products"));

    axios.get(`${process.env.REACT_APP_API_BASEURL}/employees`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then((res) => {
      const employees = res.data;
      const options = employees.map((emp) => ({
        label: emp.employeeName,
        value: emp._id,
      }));
      setRequestFromOptions(options);
    }).catch(() => message.error("Failed to load employees"));

    axios.get(`${process.env.REACT_APP_API_BASEURL}/address`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then((res) => {
      const options = res.data.map((item) => ({
        label: `${item.addressDetails}, ${item.city}`,
        value: item._id,
      }));
      setAddressOptions(options);
    }).catch(() => message.error("Failed to load addresses"));
  }, [token]);

  const handleProductChange = (index, key, value) => {
    const updated = [...products];
    updated[index][key] = value;
    setProducts(updated);
  };

  const handleAddProduct = () => {
    setProducts([...products, { productId: null, quantity: 1, category: null }]);
  };

  const handleSubmit = () => {
    if (
      !orderStatus ||
      !requestFrom ||
      !address ||
      products.some((p) => !p.productId || !p.category || !p.quantity)
    ) {
      message.error("Please fill all required fields");
      return;
    }

    const orderPayload = {
      product: products.map((p) => ({
        product_id: p.productId,
        quantity: p.quantity,
        orderType: p.category,
        productName: productOptions.find(opt => opt.value === p.productId)?.label,
      })),
      orderStatus,
      requestFrom,
      address,
      remarks,
    };

    axios
      .post(`${process.env.REACT_APP_API_BASEURL}/orders`, orderPayload, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(() => {
        message.success("Order added successfully!");
        navigate(-1);
      })
      .catch(() => {
        message.error("Failed to add order");
      });
  };

  return (
    <Card title="Add Order">
      {products.map((product, index) => (
        <Row gutter={16} key={index} style={{ marginBottom: 12 }}>
          <Col span={8}>
            <Text strong>Product Name</Text>
            <Select
              showSearch
              placeholder="Select Product"
              value={product.productId}
              onChange={(value) => handleProductChange(index, "productId", value)}
              style={{ width: "100%" }}
            >
              {productOptions.map((prod) => (
                <Option key={prod.value} value={prod.value}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {prod.images && prod.images.length > 0 && (
                      <img
                        src={`data:image/jpeg;base64,${prod.images[0]}`}
                        alt=""
                        style={{ width: 24, height: 24, marginRight: 8, objectFit: 'cover', borderRadius: 2 }}
                      />
                    )}
                    {prod.label}
                  </div>
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={8}>
            <Text strong>Quantity</Text>
            <Input
              type="number"
              value={product.quantity}
              onChange={(e) =>
                handleProductChange(index, "quantity", parseInt(e.target.value))
              }
              placeholder="Enter Quantity"
            />
          </Col>

          <Col span={8}>
            <Text strong>Product Category</Text>
            <Select
              showSearch
              placeholder="Select Category"
              value={product.category}
              onChange={(value) => handleProductChange(index, "category", value)}
              style={{ width: "100%" }}
            >
              <Option value="POSM">POSM</Option>
              <Option value="Assets">Assets</Option>
            </Select>
          </Col>
        </Row>
      ))}

      {/* Row 2: Add Product Button */}
      <Row>
        <Col span={24}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddProduct} style={{ width: 120, marginBottom: 24, backgroundColor: PRIMARY_RED }}>
            Add Product
          </Button>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Text strong>Order Status</Text>
          <Row>
            <Select
              showSearch
              placeholder="Select Order Status"
              value={orderStatus}
              onChange={setOrderStatus}
              style={{ width: "100%" }}
            >
              {orderStatusOptions.map((status) => (
                <Option key={status.value} value={status.value}>
                  {status.label}
                </Option>
              ))}
            </Select>
          </Row>
        </Col>

        <Col span={12}>
          <Text strong>Request From</Text>
          <Row>
            <Select
              showSearch
              placeholder="Select Employee"
              value={requestFrom}
              onChange={setRequestFrom}
              style={{ width: "100%" }}
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
            >
              {requestFromOptions.map((emp) => (
                <Option key={emp.value} value={emp.value}>
                  {emp.label}
                </Option>
              ))}
            </Select>
          </Row>
        </Col>
      </Row>

      <Row style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Text strong>Address</Text>
          <Row>
            <Select
              showSearch
              placeholder="Select Address"
              value={address}
              onChange={setAddress}
              style={{ width: 300 }}
            >
              {addressOptions.map((addr) => (
                <Option key={addr.value} value={addr.value}>
                  {addr.label}
                </Option>
              ))}
            </Select>
          </Row>
        </Col>
      </Row>

      <Row style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Text strong>Remarks</Text>
          <Input.TextArea
            rows={2}
            placeholder="Enter remarks (optional)"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleSubmit} style={{ backgroundColor: PRIMARY_RED }}>
            Add Order
          </Button>
        </Col>
        <Col>
          <Button type="link" style={{ color: PRIMARY_RED }} icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
        </Col>
      </Row>
    </Card>
  );
};

export default AddOrder;
